import { MapPin, Globe, Route } from "lucide-react";
import { Link } from "react-router-dom";
import {
  groupByQuizrun,
  getQuizrunRange,
  type RegistrationEvent,
  type QuizrunGroup,
} from "@/lib/registrationUtils";
import { formatQuizrunLabel, safeHref } from "@/lib/utils";
import { formatMoney, pwywSymbol } from "@/lib/currency";
import { getEventStatusForDates, isStartingSoon } from "@/lib/events";
import { EventStatusBadge } from "@/components/events/EventStatusBadge";
import { KindBadge } from "@/components/events/KindBadge";
import { Card, CardContent } from "@/components/ui/card";

interface RegistrationCardsProps {
  events: RegistrationEvent[];
  showHeader?: boolean;
  maxQuizruns?: number;
  /** Landing mode: render groups as flat header+rows, no Card wrapper/grid. */
  flat?: boolean;
  /** Landing: virtual has its own sidebar CTA, so don't duplicate it inline. */
  hideVirtual?: boolean;
  /** Show the per-group 'Quizrun #N + dates' heading. Off on the event page
   *  (the page title already names the edition); on for landing. */
  showHeading?: boolean;
}

/** Format a date as 'Sat, May 17 · 10:00 AM' */
const formatCompactDate = (iso: string, tz?: string): string => {
  const opts: Intl.DateTimeFormatOptions = {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };
  if (tz) opts.timeZone = tz;
  const parts = new Intl.DateTimeFormat("en-US", opts).formatToParts(
    new Date(iso),
  );
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("weekday")}, ${get("month")} ${get("day")} · ${get("hour")}:${get("minute")} ${get("dayPeriod")}`;
};

/** Format a date range as 'Jul 5 – 12', collapsing the shared month/year.
 *  Formatted in UTC: quizrun windows are stored as UTC timestamps, so the
 *  canonical edition window must read the same in every client timezone
 *  (local formatting shifts a day for negative-offset users). */
const formatCompactRange = (
  startIso: string,
  endIso?: string | null,
): string => {
  const s = new Date(startIso);
  const sMonth = s.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
  const sDay = s.getUTCDate();
  const start = `${sMonth} ${sDay}`;
  if (!endIso) return start;
  const e = new Date(endIso);
  if (sameUtcDay(s, e)) return start;
  const eMonth = e.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
  // Same month → 'Jul 5 – 12'; different month → 'Jul 28 – Aug 2'.
  const end = sMonth === eMonth ? `${e.getUTCDate()}` : `${eMonth} ${e.getUTCDate()}`;
  return `${start} – ${end}`;
};

/** True when two dates fall on the same calendar day in UTC. */
const sameUtcDay = (a: Date, b: Date): boolean =>
  a.getUTCFullYear() === b.getUTCFullYear() &&
  a.getUTCMonth() === b.getUTCMonth() &&
  a.getUTCDate() === b.getUTCDate();


/** Full-month date range, no year, month-first: 'June 13 – July 12'
 *  (or 'June 13' single). UTC-formatted (see formatCompactRange). */
/** Clickable group header: 'Quizrun #N' + date-range pill + a link to the
 *  event/results page, plus a hint line. Shared by landing (flat) and the
 *  carded events view so both stay in sync. `hideVirtual` mirrors the list so
 *  the hint matches what renders. `showEventLink` is off on the event page
 *  itself (already there — link would be circular). The date pill uses the
 *  Quizrun entity's own range (getQuizrunRange), matching the page title. */
const QuizrunHeader = ({
  group,
  hideVirtual = false,
  showEventLink = true,
}: {
  group: QuizrunGroup;
  hideVirtual?: boolean;
  showEventLink?: boolean;
}) => {
  const range = getQuizrunRange(group);
  const inner = (
    <>
      <span className="text-base font-extrabold tracking-tight text-primary">
        {formatQuizrunLabel(group.quizrunNumber, group.quizrunLanguage, group.quizrunDateStart, { title: group.quizrunTitle })}
      </span>
      {/* Regular/Special kind badge — consistent with the Events page cards.
          Specials have no #number to read as regular, so this list marks kind
          inline on every edition. */}
      <KindBadge kind={group.quizrunKind === "special" ? "special" : "standard"} />
      <span className="inline-flex items-center whitespace-nowrap rounded-full border border-border bg-muted/60 px-2.5 py-0.5 text-xs font-semibold text-muted-foreground">
        {formatCompactRange(range.start, range.end)}
      </span>
      {showEventLink && (
        <span className="ml-auto inline-flex items-center gap-1 whitespace-nowrap text-xs font-semibold text-primary opacity-80 group-hover/qh:opacity-100 transition-opacity">
          Event &amp; results →
        </span>
      )}
    </>
  );
  return (
    <div className="space-y-0.5">
      {showEventLink ? (
        <Link
          to={`/events/${group.quizrunId}`}
          className="group/qh flex items-center gap-2.5 flex-wrap"
        >
          {inner}
        </Link>
      ) : (
        <div className="flex items-center gap-2.5 flex-wrap">{inner}</div>
      )}
      <p className="text-xs text-muted-foreground">
        Pick your city{!hideVirtual && group.virtualEvents.length > 0 ? " — or run it virtually." : " — one registration per event."}
      </p>
    </div>
  );
};

/** Per-event (per city/leg) status badge for a registration row. Each leg
 *  computes its OWN status from its own date_start/date_end, so a quizrun can
 *  have Berlin live ("In Progress") while its Virtual leg is still upcoming
 *  ("Registration Open"). Green "Registration Open" always shows for a
 *  non-finished leg; at most one contextual pill (In Progress / Almost here /
 *  Last chance). Finished legs show a single grey "Completed". */
const EventRowStatus = ({ evt }: { evt: RegistrationEvent }) => {
  const status = getEventStatusForDates(evt.date_start, evt.date_end);
  const startsSoon = status === "registration-open" && isStartingSoon(evt.date_start);
  return (
    <EventStatusBadge
      status={status}
      dateFinish={evt.date_end}
      isVirtual={evt.isVirtual}
      startsSoon={startsSoon}
    />
  );
};

/** Tiered price indicator, right-aligned in a row's CTA cluster. A real
 *  price_cents renders that amount as a solid chip (specials). No price set →
 *  Quizrun's pay-what-you-want model, shown inline as '💜 <cur>5–20+' (range visible
 *  without a tooltip, no 'PWYW' jargon). The heart matches the donation modal. */
const PriceIndicator = ({ evt }: { evt: RegistrationEvent }) => {
  const priced = evt.priceCents != null && evt.priceCents > 0;
  if (priced) {
    return (
      <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-bold text-primary">
        {formatMoney(evt.priceCents as number, evt.priceCurrency)}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary"
      title="Pay what you want — your entry is free; donate if you'd like to support Quizrun"
    >
      <span aria-hidden>💜</span> {pwywSymbol(evt.priceCurrency)}5–20+
    </span>
  );
};

/** Compact onsite location row */
const OnsiteRow = ({ evt }: { evt: RegistrationEvent }) => {
  const status = getEventStatusForDates(evt.date_start, evt.date_end);
  const startsSoon = status === "registration-open" && isStartingSoon(evt.date_start);
  // Left accent only while live/starting-soon. Kept subtle (the status pill
  // already carries the meaning) so rows with vs without it don't clash.
  const statusBorder =
    status === "in-progress"
      ? "border-l-2 border-l-amber-400/60"
      : startsSoon
        ? "border-l-2 border-l-orange-400/50"
        : "";

  // Venue name only when it adds info beyond the city (evt.name === city).
  const venue =
    evt.locationName && evt.locationName !== evt.name ? evt.locationName : null;
  const mapHref = safeHref(evt.locationLink);
  const routeHref = safeHref(evt.routeLink);
  const showMeetingPoint = venue || routeHref;

  // The whole card registers on click via a full-bleed overlay link (z-0).
  // Meeting-point / route links sit above it (relative z-10) so they open the
  // map without nesting <a> inside <a>.
  const overlay = (
    <Link
      to={`/events/register?event_id=${evt.eventId}`}
      aria-label={`Register — ${evt.name}`}
      className="absolute inset-0 z-0"
    />
  );

  return (
    <div
      className={`group relative px-3.5 py-3 hover:bg-muted/40 transition-colors ${statusBorder}`}
    >
      {overlay}
      {/* Cap the inner row width so on wide containers (event page = max-w-7xl)
          the price/CTA cluster stays next to the event text instead of drifting
          to the far edge with a large dead gap ('payment info too far'). */}
      <div className="relative z-[1] mx-auto flex max-w-3xl items-start gap-2.5 pointer-events-none">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold">{evt.name}</span>
            <EventRowStatus evt={evt} />
          </div>
          <div className="text-xs text-muted-foreground">
            {formatCompactDate(evt.date_start, evt.timezone)}
          </div>
        </div>
        <div className="mt-0.5 flex shrink-0 flex-col items-end gap-1">
          {/* Tiered price: real amount → chip; unset → inline '💜 <cur>5–20+' PWYW. */}
          <PriceIndicator evt={evt} />
          {/* Single CTA — quiet by default, lights up on row hover/focus (mobile
              has no hover so it stays legible via the text-primary colour). */}
          <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-semibold text-primary opacity-70 transition-opacity group-hover:opacity-100">
            Register →
          </span>
        </div>
      </div>

      {/* Meeting point + Route as two quiet icon links (no 'Meeting point:'
          label — the pin carries the meaning). The row title is icon-free, so
          the single MapPin lives here on the location line. */}
      {showMeetingPoint && (
        <div className="relative z-10 mx-auto mt-1 flex max-w-3xl items-center gap-3.5 text-xs text-muted-foreground pointer-events-auto">
          {venue && (
            mapHref ? (
              <a
                href={mapHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-primary hover:underline underline-offset-2"
              >
                <MapPin className="h-3 w-3 shrink-0" />
                {venue}
              </a>
            ) : (
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-3 w-3 shrink-0" />
                {venue}
              </span>
            )
          )}
          {routeHref && (
            <a
              href={routeHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline underline-offset-2 whitespace-nowrap"
            >
              <Route className="h-3 w-3 shrink-0" />
              Route
            </a>
          )}
        </div>
      )}
    </div>
  );
};

/** Virtual event row — distinct from the fixed-location city cards but kept
 *  quieter than them (they're the primary CTA). Dashed = flexible/anytime.
 *  Two lines: title + a subtitle ('Track your run…') and the quizrun's
 *  start–finish window (the period the virtual run is open for). */
const VirtualRow = ({ evt }: { evt: RegistrationEvent }) => {
  const rowClass =
    "block px-3.5 py-3 bg-muted/10 hover:bg-muted/30 transition-colors group";
  // Inner content capped + centered so on wide containers the price/CTA stays
  // near the text (matches the onsite card), not pinned to the far edge.
  const inner = "mx-auto flex max-w-3xl items-center gap-2.5";

  // Quizrun start–finish window — virtual is open for the whole edition.
  // Compact range ('Sep 4 – Oct 11') to match the onsite rows in the same
  // group; mixing compact + full-month dates side by side read as unfinished.
  const windowLabel = evt.quizrunDateStart
    ? formatCompactRange(evt.quizrunDateStart, evt.quizrunDateFinish || null)
    : null;

  const content = (
    <>
      <div className="flex-1 min-w-0">
        <span className="flex items-center gap-2 flex-wrap text-sm font-semibold">
          Virtual
          <EventRowStatus evt={evt} />
        </span>
        {/* Date first (matches onsite rows: name → date → place), then the
            'run anywhere' meaning line below it. */}
        {windowLabel && (
          <span className="block text-xs text-muted-foreground">{windowLabel}</span>
        )}
        {/* Single icon lives on the meaning line (not the title) — the globe
            explains 'run anywhere', so it belongs next to that phrase. */}
        <span className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <Globe className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>Anywhere, anytime — track your run with a phone or watch</span>
        </span>
      </div>
      <div className="mt-0.5 flex shrink-0 flex-col items-end gap-1">
        <PriceIndicator evt={evt} />
        {/* Single CTA to the in-app registration. */}
        <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-semibold text-primary opacity-70 transition-opacity group-hover:opacity-100">
          Register →
        </span>
      </div>
    </>
  );

  return (
    <Link to={`/events/register?event_id=${evt.eventId}`} className={rowClass}>
      <div className={inner}>{content}</div>
    </Link>
  );
};

/** Renders a single quizrun group's events with onsite/virtual separation */
const QuizrunEventList = ({
  group,
  twoColumnOnsite = false,
  hideVirtual = false,
}: {
  group: QuizrunGroup;
  /** Desktop: flow onsite city rows into 2 columns (wide single-quizrun view). */
  twoColumnOnsite?: boolean;
  /** Suppress inline virtual rows (landing uses a dedicated sidebar CTA). */
  hideVirtual?: boolean;
}) => {
  const { onsiteEvents, virtualEvents } = group;
  const hasOnsite = onsiteEvents.length > 0;
  const hasVirtual = !hideVirtual && virtualEvents.length > 0;

  const onsiteLayout =
    twoColumnOnsite && onsiteEvents.length > 1
      ? "grid gap-1.5 lg:grid-cols-2"
      : "divide-y divide-border/60";

  return (
    <div className="overflow-hidden rounded-xl border border-border/60 bg-muted/20">
      {hasOnsite && (
        <div className={onsiteLayout}>
          {onsiteEvents.map((evt) => (
            <OnsiteRow key={evt.id} evt={evt} />
          ))}
        </div>
      )}
      {hasOnsite && hasVirtual && (
        <div className="border-t border-border/60" />
      )}
      {hasVirtual && (
        <div className="divide-y divide-border/60">
          {virtualEvents.map((evt) => (
            <VirtualRow key={evt.id} evt={evt} />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Displays registration list — compact rows per location.
 * When only a single quizrun group exists, renders without card wrapper.
 */
export const RegistrationCards = ({
  events,
  showHeader = true,
  maxQuizruns,
  flat = false,
  hideVirtual = false,
  showHeading = true,
}: RegistrationCardsProps) => {
  if (events.length === 0) return null;

  let groups = groupByQuizrun(events);
  if (maxQuizruns) {
    // Cap the standard series (numbered editions) to the most relevant few, but
    // always keep every special quizrun so one-off events aren't sliced off the
    // landing page just because a couple of numbered editions sort ahead of them.
    let standardShown = 0;
    groups = groups.filter((g) => {
      if (g.quizrunKind === "special") return true;
      standardShown += 1;
      return standardShown <= maxQuizruns;
    });
  }

  // Landing: flat header + rows per group, no Card/grid nesting.
  // Checked before the single-group branch so the landing keeps its flat styling
  // (and the heading) even when only one Quizrun is upcoming. `showHeading`
  // toggles the per-group 'Quizrun #N + dates' heading on/off.
  if (flat) {
    // When virtual is hidden (sidebar owns it), drop groups left with no onsite
    // events so we don't render a dangling header over an empty list.
    const visibleGroups = hideVirtual
      ? groups.filter((g) => g.onsiteEvents.length > 0)
      : groups;
    return (
      <div className="space-y-6">
        {visibleGroups.map((group) => (
          <div key={group.quizrunId} className="space-y-2">
            {showHeading && (
              <QuizrunHeader group={group} hideVirtual={hideVirtual} />
            )}
            <QuizrunEventList group={group} hideVirtual={hideVirtual} />
          </div>
        ))}
      </div>
    );
  }

  // Single-quizrun compact mode (event page): no card wrapper and no group
  // heading — you're already on that edition's page (title/breadcrumb name it).
  if (groups.length === 1) {
    const group = groups[0];
    return (
      <div className="space-y-4">
        {showHeader && (
          <h2 className="text-2xl font-bold text-foreground">Register Now</h2>
        )}
        <div className="space-y-2">
          {showHeading && (
            <QuizrunHeader group={group} hideVirtual={hideVirtual} showEventLink={false} />
          )}
          <QuizrunEventList group={group} twoColumnOnsite hideVirtual={hideVirtual} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {showHeader && (
        <h2 className="text-2xl font-bold text-foreground">Register Now</h2>
      )}

      <div
        className={`grid gap-6 ${groups.length === 2 ? "sm:grid-cols-2 max-w-3xl mx-auto" : "md:grid-cols-2 lg:grid-cols-3"}`}
      >
        {groups.map((group) => (
          <Card
            key={group.quizrunId}
            className="hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-2 flex flex-col justify-center"
          >
            <CardContent className="space-y-2 p-4">
              <QuizrunHeader group={group} />
              <QuizrunEventList group={group} />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
